import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TypedForm } from '@models/support/interfaces';
import { ISourceItemData } from '@se/models';
import { BehaviorSubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

export interface IAssignEquipmentDialogDataProps {
    message: string;
    body?: string;
    cancelLabel?: string;
    confirmLabel?: string;
    panelClass?: string;
    confirmOnEnter?: boolean;
    equipments: ISourceItemData[];
    selectedEquipment: ISourceItemData | undefined;
    idExamination: string;
    changeEquipment: (idExamination: string, equipment: ISourceItemData) => Promise<boolean>;
}

interface IAssignEquipmentForm {
    equipment: string;
    equipmentFilterCtrl: string | null;
}
@Component({
    selector: 'se-assign-equipment-dialog',
    templateUrl: './se-assign-equipment-dialog.component.html',
    styleUrls: ['./se-assign-equipment-dialog.component.scss'],
    standalone: false,
})
export class SeAssignEquipmentDialogComponent implements OnInit, OnDestroy {
    confirmLabel = 'RadioReadAssign';
    cancelLabel = 'Cancel';
    isWaiting: boolean = false;
    form: FormGroup<TypedForm<IAssignEquipmentForm>> | null = null;

    filteredEquipments$: BehaviorSubject<ISourceItemData[]> = new BehaviorSubject<ISourceItemData[]>([]);

    private _onDestroy = new Subject<void>();

    constructor(
        private dialogRef: MatDialogRef<IAssignEquipmentDialogDataProps, boolean>,
        @Inject(MAT_DIALOG_DATA) public data: IAssignEquipmentDialogDataProps,
    ) {
        this.confirmLabel = this.data.confirmLabel ?? this.confirmLabel;
        this.cancelLabel = this.data.cancelLabel ?? this.cancelLabel;
    }

    ngOnInit(): void {
        this.form = new FormGroup<TypedForm<IAssignEquipmentForm>>({
            equipment: new FormControl<string>(this.data.selectedEquipment?.guid ?? '', {
                nonNullable: true,
                validators: [Validators.required],
            }),
            equipmentFilterCtrl: new FormControl<string | null>(null),
        });

        const classesToAdd: string[] = ['se-assign-equipment-dialog'];
        if (this.data.panelClass) classesToAdd.push(this.data.panelClass);
        this.dialogRef.addPanelClass(classesToAdd);
        this.filteredEquipments$.next(this.data.equipments);
        this.form.controls.equipmentFilterCtrl.valueChanges.pipe(takeUntil(this._onDestroy)).subscribe(this._filter);
    }

    ngOnDestroy() {
        this._onDestroy.next();
        this._onDestroy.complete();
    }

    private _filter = () => {
        if (!this.data.equipments) return [];

        const search = (this.form?.controls.equipmentFilterCtrl.value ?? '').toLowerCase();
        if (!search) {
            this.filteredEquipments$.next([...this.data.equipments]);
            return;
        }
        this.filteredEquipments$.next(
            this.data.equipments.filter((eq) => eq.description.toLowerCase().indexOf(search) > -1),
        );
    };

    async confirm() {
        this.dialogRef.disableClose = true;
        this.isWaiting = true;
        const guid = this.form?.value.equipment ?? '';
        const selectedEquipement = this.data.equipments.find((item) => item.guid === guid);
        if (this.form && this.form.valid && selectedEquipement) {
            const changed = await this.data.changeEquipment(this.data.idExamination, selectedEquipement);
            if (changed) this.dialogRef.close(true);
        }
        this.dialogRef.disableClose = false;
        this.isWaiting = false;
    }

    closeDialog() {
        this.dialogRef.close();
    }
}
